// frontend/src/Administration.tsx
// =============================================================================
// DIESE DATEI STEUERT DEN ADMINISTRATIONS-BEREICH
// Hier werden Projekte verwaltet (Neu, Öffnen, Speichern, Löschen),
// globale Einstellungen vorgenommen und die Editoren für Dimensionen,
// Relationen und Bedingungen eingebunden.
// =============================================================================
import React, { useEffect, useMemo, useState } from "react";
import type { DataModel } from "./AppTypes";
import CapacityEditor from "./CapacityEditor";
import { Mode, getBaseColor } from "./colorUtils";
import AdjustmentEditor from "./AdjustmentEditor";
import ConditionEditor, { ExtendedCondition } from "./ConditionEditor";
import CreationEditor from "./CreationEditor";
import DimensionEditor from "./DimensionEditor";
import MetricEditor from "./MetricEditor";
import ObjectiveEditor from "./ObjectiveEditor";
import FixationEditor from "./FixationEditor";
import RelationEditor from "./RelationEditor";
import SettingEditor from "./SettingEditor";
import TimingEditor from "./TimingEditor";

import Chat, { clearChatStorageForProject } from "./Chat";
import { xmlFromData, xmlToData } from "./xmlHandling";

import { renameOrganization } from "./api/OrganizationClient";
import {
  createNewProject,
  deleteProject,
  duplicateProject,
  openProject,
  renameProject,
  updateSolverMode
} from "./api/ProjectClient";
import { sendRulesXml } from "./api/RulesClient";
// import { startPlanPolling } from "./api/PlanClient"; // <-- nicht mehr hier benötigt
import { notifyProjectActivity } from "./api/pingClient";
import { importPlan } from "./api/PlanClient";
import { exportPlanCsv, importPlanCsv } from "./csvImport_Export";
import { ProjectDetail, UserInfo } from "./model/backendTypes";
import OrganizationDropdown from "./OrganizationDropdown";
import ProjectNewDropdown from "./ProjectNewDropdown";
import { exportRulesXml, importRulesXml } from "./xmlImport_Export";

// Definition der Eingabe-Parameter (Props) für die Administration-Komponente
type Props = {
  user: UserInfo | null;
  data: DataModel | null;
  setData: React.Dispatch<React.SetStateAction<DataModel | null>>;
  mode: Mode;


  // Neu: Das aktuelle Projekt kommt von oben (App.tsx)
  project: ProjectDetail | null;
  // Callback, wenn wir ein neues Projekt laden wollen (New, Open, Duplicate, Delete)
  onProjectChange: (newProj: ProjectDetail | null) => void;
  // Callback für reine Metadaten-Updates (z.B. solver_mode) ohne DataModel-Reset
  onProjectMetaChange: (updatedProj: ProjectDetail) => void;
  // Callback, um den User im Parent zu aktualisieren (z.B. bei Organisationsnamen-Änderung)
  onUserChange: (newUser: UserInfo | null) => void;
};

// Chat imported separately



// Hilfsfunktion zur Formatierung von Zeitstempeln (Datum & Uhrzeit)
// Falls kein Zeitstempel vorhanden ist, wird ein Platzhalter "–" zurückgegeben.
function fmt(ts?: string | null): string {
  if (!ts) return "–";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "–";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function SolveCountdown({ estimateAt, lockedAt }: { estimateAt: string, lockedAt?: string | null }) {
  const [timeLeft, setTimeLeft] = useState(0);
  const [passedTime, setPassedTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const end = new Date(estimateAt).getTime();
    if (isNaN(end)) return;
    const start = lockedAt ? new Date(lockedAt).getTime() : Date.now();
    const total = Math.max(1000, end - start);

    const compute = () => {
      const remaining = Math.max(0, end - Date.now());
      setTimeLeft(remaining);
      const passed = Math.max(0, Date.now() - start);
      setPassedTime(passed);
      const p = Math.min(100, (passed / total) * 100);
      setProgress(p);
    };
    compute();
    const timer = setInterval(compute, 1000);
    return () => clearInterval(timer);
  }, [estimateAt, lockedAt]);

  if (timeLeft === 0) return null;

  const m = Math.floor(timeLeft / 60000);
  const s = Math.floor((timeLeft % 60000) / 1000);
  const pad = (n: number) => n.toString().padStart(2, '0');

  const passedS = Math.floor(passedTime / 1000);

  return (
    <div 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ display: "flex", alignItems: "center", gap: "8px", width: "120px", cursor: "help", position: "relative" }}
    >
      <div style={{ flex: 1, background: "rgba(0,0,0,0.1)", height: "6px", borderRadius: "3px", overflow: "hidden" }}>
        <div style={{ width: `${progress}%`, background: "currentColor", opacity: 0.6, height: "100%", transition: "width 1s linear" }} />
      </div>
      <span style={{ fontSize: "0.85em", opacity: 0.8, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
        {m}:{pad(s)}
      </span>
      {isHovered && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          marginTop: "6px",
          padding: "6px 10px",
          backgroundColor: "#f5f5f5",
          border: "1px solid #c0c0c0",
          borderRadius: "4px",
          color: "#222",
          fontSize: "13px",
          fontWeight: 400,
          zIndex: 1000,
          whiteSpace: "nowrap",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
        }}>
          {passedS}s
        </div>
      )}
    </div>
  );
}

// Hauptkomponente für die Administration
// Verwaltet den Zustand der verschiedenen Editoren und die Kommunikation mit dem Backend.
export default function Administration({
  user,
  data,
  setData,
  mode,
  project,
  onProjectChange,
  onProjectMetaChange,
  onUserChange,
}: Props) {
  // Lokaler State, um zu steuern, welcher Editor-Bereich (Settings, Dimensions, etc.) gerade ausgeklappt ist.
  const [activeSection, setActiveSection] = useState<string | null>("chat");

  // ---------- Zentraler RulesChange-Handler ----------
  // Aktualisiert die lokalen Daten und synchronisiert Änderungen sofort mit dem Backend.
  const handleRulesChange = (
    updater: (prev: DataModel) => DataModel
  ) => {
    setData((prev) => {
      if (!prev) return prev;
      const next = updater(prev);

      // Backend-Update: xmlFromData → sendRulesXml
      if (user && project) {
        try {
          const xml = xmlFromData(next);

          // Synchronisiere auch den XML-Editor (bidirektionale Sync)
          setXmlText(xml);
          setLastSaved(xml);

          sendRulesXml(user.id, project.id, xml).catch((err) => {
            console.error("Rules update failed:", err);
          });
        } catch (e) {
          console.error("xmlFromData failed:", e);
        }
      }

      return next;
    });
  };

  // ---------- Projekt-Buttons (nur hier!) ----------
  // Diese Funktionen steuern die Projektverwaltung (CRUD-Operationen).

  // Erstellt ein neues Projekt basierend auf einem Template.
  const handleNew = async (templateName: string, templateXml: string) => {
    if (!user) return;
    try {
      const proj = await createNewProject(user.id);

      await sendRulesXml(user.id, proj.id, templateXml);
      proj.rules_xml = templateXml;
      onProjectChange(proj);
    } catch (e: any) {
      console.error("Creating a new project failed:", e);

      const detail = e?.response?.data?.detail ?? e?.detail ?? e?.message;

      if (detail === "project-limit, please upgrade") {
        alert("Project limit reached, please upgrade");
      } else {
        alert("Could not create project");
      }
    }
  };

  // Dupliziert das aktuell geöffnete Projekt.
  const handleDuplicate = async () => {
    if (!user || !project) return;
    try {
      const proj = await duplicateProject(user.id, project.id);
      onProjectChange(proj);
    } catch (e: any) {
      console.error("Duplication failed:", e);

      const detail = e?.response?.data?.detail ?? e?.detail ?? e?.message;

      if (detail === "project-limit, please upgrade") {
        alert("Project limit reached, please upgrade");
      } else {
        alert("Could not duplicate project.");
      }      
    }
  };

  // Löscht das aktuelle Projekt nach einer Sicherheitsabfrage.
  const handleDelete = async () => {
    if (!user || !project) return;
    if (!window.confirm("Do you really want to delete this project?")) return;
    try {
      const deletedId = project.id;
      const newActive = await deleteProject(user.id, deletedId);
      clearChatStorageForProject(deletedId);
      onProjectChange(newActive);
    } catch (e) {
      console.error("Deletion failed:", e);
      alert("The project could not be deleted");
    }
  };

  // ---------- Hilfsdaten für Validierung (wie 0.4) ----------
  // Sammelt alle Namen (Dimensionen, Tags, Punkte, Conditions, Relations)
  // für Validierungszwecke (z.B. Eindeutigkeit von Namen).
  const allNames = useMemo(() => {
    const names: string[] = [];

    // WICHTIG: Erst auf null prüfen, dann erst auf data.* zugreifen
    if (!data) {
      return names;
    }

    data.dimensions.forEach((d) => {
      names.push(d.name);
      d.tags.forEach((t) => names.push(t.name));
      d.points.forEach((p) => names.push(p.name));
    });
    data.conditions.forEach((c) => names.push(c.name));
    data.relations.forEach((r) => names.push(r.name));
    if (data.creations) data.creations.forEach((c) => names.push(c.name));
    if (data.capacities) data.capacities.forEach((c) => names.push(c.name));
    if (data.metrics) data.metrics.forEach((m) => names.push(m.name));

    return names;
  }, [data]);

  // ---------- Import/Export (kann weitgehend aus 0.4 übernommen werden) ----------
  const handleImportRules = () => {
    importRulesXml((parsed) => {
      // parsed: DataModel
      handleRulesChange(() => parsed);
    });
  };

  const handleExportRules = () => {
    if (!data) return;
    const projectName = project ? project.name : "rules";
    exportRulesXml(data, projectName);
  };

  const handleExportCsv = () => {
    if (!data) return;
    const projectName = project ? project.name : "plan";
    exportPlanCsv(data.plan_csv, projectName);
  };

  const handleImportPlan = () => {
    if (!user || !project) return;

    importPlanCsv(
      async (csvText) => {
        try {
          // Backend: clean + compute fulfillments + store
          await importPlan(user.id, project.id, csvText);

          // Danach: Projekt reloaden (Single Source of Truth)
          const updatedProject = await openProject(user.id, project.id);
          onProjectChange(updatedProject);
        } catch (e) {
          console.error("Import plan failed:", e);
          alert(String(e));
        }
      },
      (msg) => alert(msg)
    );
  };  
  
  // ---------- RENDER ----------
  // Aufbau der Benutzeroberfläche

  // Fallback, falls keine Projektdaten geladen sind.
  if (!data) {
    return (
      <>
        <h1>Administration</h1>
        <p>Kein Projekt geladen. Bitte logge dich ein oder lege ein neues Projekt an.</p>
      </>
    );
  }
  // Lokaler State für den XML-Editor
  const [xmlText, setXmlText] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  
  const [lastSaved, setLastSaved] = useState<string>("");
  const [loadedProjectId, setLoadedProjectId] = useState<number | null>(null);

  // XML nur aus data übernehmen, wenn ein NEUES Projekt geladen wurde.
  // Verhindert, dass während des Tippens (und anschließendem Auto-Reload) der Text überschrieben wird.
  React.useEffect(() => {
    if (data && project && project.id !== loadedProjectId) {
      try {
        // Versuchen, das originale XML zu nehmen, falls vorhanden, sonst generieren
        const xml = data.rules_xml || xmlFromData(data);
        setXmlText(xml);
        setLastSaved(xml);
        setLoadedProjectId(project.id);
      } catch (e) {
        console.error("xmlFromData failed:", e);
      }
    }
  }, [data, project, loadedProjectId]);

  // Auto-Save mit Debounce: Speichert automatisch 1 Sekunde nach dem letzten Tastendruck
  React.useEffect(() => {
    // Nur speichern wenn sich der Text geändert hat und initiale Daten geladen sind
    if (!xmlText || xmlText === lastSaved || !user || !project) {
      return;
    }

    setIsSaving(true);
    const timeoutId = setTimeout(async () => {
      try {
        console.log("Auto-saving XML...");
        await sendRulesXml(user.id, project.id, xmlText);
        setLastSaved(xmlText);
        console.log("XML automatically saved");

        // Nach einer kurzen Verzögerung das Projekt neu laden
        setTimeout(async () => {
          try {
            const updatedProject = await openProject(user.id, project.id);
            onProjectChange(updatedProject);
          } catch (e) {
            console.error("Failed to reload project:", e);
          }
        }, 1000);

      } catch (e) {
        console.error("Auto-save failed:", e);
      } finally {
        setIsSaving(false);
      }
    }, 300); // 300ms Verzögerung

    return () => clearTimeout(timeoutId);
  }, [xmlText, user, project, lastSaved, onProjectChange]);

  // Auto-Save mit Debounce: Speichert automatisch 1 Sekunde nach dem letzten Tastendruck
  return (
    <>
      <h1>Administration</h1>

      {project && user && (
        <div className="quality-display" style={{ paddingRight: "4px" }}>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px", flex: 1, minWidth: 0 }}>
            <span style={{ whiteSpace: "nowrap", fontSize: "0.95em", fontWeight: "bold", color: "var(--quality-text, #2d5aa0)" }}>
              {project.id}: {project.scheduler_assessment ?? "–"}
            </span>
            {project.solve_estimate_at && (project.scheduler_assessment === "?" || project.scheduler_assessment === "solving" || project.scheduler_assessment === "waiting") && (
              <SolveCountdown estimateAt={project.solve_estimate_at} lockedAt={project.solve_locked_at} />
            )}
          </div>
          <div className="solver-mode-group" style={{ marginRight: "-12px" }}>
            {/* Two-way switch: off | on */}
            <div className="solver-toggle-row">
              <button
                className={`solver-toggle-switch ${(project.solver_mode ?? "on") !== "off" ? "solver-toggle-switch--on" : ""}`}
                onClick={async () => {
                  const next = (project.solver_mode ?? "on") === "off" ? "on" : "off";
                  try {
                    const updated = await updateSolverMode(user.id, project.id, next);
                    onProjectMetaChange(updated);
                  } catch(e) { console.error(e); }
                }}
                aria-label="off / on"
              >
                <span className="solver-toggle-thumb" />
              </button>
              <span className="solver-toggle-label solver-toggle-label--active">
                {(project.solver_mode ?? "on") === "off" ? "off" : "on"}
              </span>
            </div>

           </div>
        </div>
      )}

      <div className="admin-buttons">
        <ProjectNewDropdown user={user} onSelectTemplate={handleNew} disabled={!user} />
        <button onClick={handleDuplicate} disabled={!user || !project}>
          duplicate
        </button>
        <button onClick={handleDelete} disabled={!user || !project}>
          delete
        </button>
        

        <button onClick={handleImportRules}>import rules</button>
        <button onClick={handleExportRules}>export rules</button>
        <button onClick={handleImportPlan}>import plan</button>
        <button onClick={handleExportCsv}>export plan</button>
        <OrganizationDropdown user={user} />
      </div>

      <div
        className="settings-panel settings-panel--chat"
        style={{
          background: getBaseColor(
            2,
            "section",
            data.settings.hue ?? 180,
            mode
          ),
        }}
      >
        <div
          className="settings-panel__header"
          onClick={() =>
            setActiveSection(activeSection === "chat" ? null : "chat")
          }
        >
          <div className="settings-panel__title">
            {activeSection === "chat" ? "▾" : "▸"} Chat
          </div>
        </div>

        <div
          className="settings-panel__content"
          style={{
            display: activeSection === "chat" ? undefined : "none",
            padding: 0,
          }}
        >
          <Chat
            user={user}
            project={project}
            mode={mode}
            xmlText={xmlText}
            onXmlReceived={(xml) => {
              // Das gepatchte XML vom Chat-Assistenten muss in der DB
              // gespeichert werden, damit der Solver es nutzen kann.
              // WICHTIG: Wir rufen sendRulesXml() direkt auf (nicht
              // handleRulesChange), weil handleRulesChange das XML durch
              // xmlFromData(xmlToData(xml)) jagt, was Informationen
              // verlieren kann. Hier senden wir das Original-XML.
              setXmlText(xml);
              setLastSaved(xml);
              const newData = xmlToData(xml);
              if (newData) {
                setData(newData);
              }
              if (user && project) {
                sendRulesXml(user.id, project.id, xml).catch((err) => {
                  console.error("Failed to persist chat XML change:", err);
                });
              }
            }}
          />
        </div>
      </div>

      {project && user && (
        <SettingEditor
          settings={data.settings}
          onChange={(newSettings) =>
            handleRulesChange((prev) => ({
              ...prev,
              settings: newSettings,
            }))
          }
          isActive={activeSection === "settings"}
          toggle={() =>
            setActiveSection(activeSection === "settings" ? null : "settings")
          }
          mode={mode}
          projectId={project.id}
          projectName={project.name}
          organizationName={user.organization_name}
          onProjectNameChange={(newName) => {
            onProjectChange({ ...project, name: newName });
          }}
          onProjectNameCommit={async (newName) => {
            try {
              const updated = await renameProject(user.id, project.id, newName);
              onProjectChange(updated);
              notifyProjectActivity(project.id).catch(() => { });
            } catch (e) {
              console.error("Failed to rename project:", e);
            }
          }}
          onOrganizationNameChange={(newName) => {
            onUserChange({ ...user, organization_name: newName });
          }}
          onOrganizationNameCommit={async (newName) => {
            try {
              if (!user) return;
              const updatedUser = await renameOrganization(user.id, newName);
              onUserChange(updatedUser);
              if (project?.id) notifyProjectActivity(project.id).catch(() => { });
            } catch (e) {
              console.error("Failed to rename organization:", e);
            }
          }}
        />
      )}

      <DimensionEditor
        dimensions={data.dimensions}
        onChange={(dims) =>
          handleRulesChange((prev) => ({
            ...prev,
            dimensions: dims,
          }))
        }
        allNames={allNames}
        isActive={activeSection === "dimensions"}
        toggle={() =>
          setActiveSection(
            activeSection === "dimensions" ? null : "dimensions"
          )
        }
        userHue={data.settings.hue}
        mode={mode}
        projectId={project?.id ?? 0}
        relations={data.relations}
        conditions={data.conditions as ExtendedCondition[]}
        onRelationsChange={(rels) =>
          handleRulesChange((prev) => ({
            ...prev,
            relations: rels,
          }))
        }
        onConditionsChange={(conds) =>
          handleRulesChange((prev) => ({
            ...prev,
            conditions: conds,
          }))
        }
        syntaxData={data.syntaxchecks_json}
      />

      <TimingEditor
        timings={data.timings ?? []}
        onChange={(timings) =>
          handleRulesChange((prev) => ({
            ...prev,
            timings,
          }))
        }
        allNames={allNames}
        isActive={activeSection === "timings"}
        toggle={() =>
          setActiveSection(
            activeSection === "timings" ? null : "timings"
          )
        }
        userHue={data.settings.hue}
        mode={mode}
        projectId={project?.id ?? 0}
        syntaxData={data.syntaxchecks_json}
      />

      <RelationEditor
        relations={data.relations}
        onChange={(rels) =>
          handleRulesChange((prev) => ({
            ...prev,
            relations: rels,
          }))
        }
        dimensions={data.dimensions}
        allNames={allNames}
        isActive={activeSection === "relations"}
        toggle={() =>
          setActiveSection(
            activeSection === "relations" ? null : "relations"
          )
        }
        userHue={data.settings.hue}
        mode={mode}
        projectId={project?.id ?? 0}
        syntaxData={data.syntaxchecks_json}
      />

      <CreationEditor
        creations={data.creations || []}
        onChange={(creations) =>
          handleRulesChange((prev) => ({
            ...prev,
            creations,
          }))
        }
        dimensions={data.dimensions}
        allNames={allNames}
        isActive={activeSection === "creations"}
        toggle={() =>
          setActiveSection(
            activeSection === "creations" ? null : "creations"
          )
        }
        userHue={data.settings.hue}
        mode={mode}
        projectId={project?.id ?? 0}
        syntaxData={data.syntaxchecks_json}
        statsData={data.fulfillments_json}
      />

      <CapacityEditor
        capacities={data.capacities || []}
        onChange={(caps) =>
          handleRulesChange((prev) => ({
            ...prev,
            capacities: caps,
          }))
        }
        dimensions={data.dimensions}
        allNames={allNames}
        isActive={activeSection === "capacities"}
        toggle={() =>
          setActiveSection(
            activeSection === "capacities" ? null : "capacities"
          )
        }
        userHue={data.settings.hue}
        mode={mode}
        projectId={project?.id ?? 0}
        syntaxData={data.syntaxchecks_json}
      />

      <ConditionEditor
        conditions={data.conditions as ExtendedCondition[]}
        groups={data.conditionGroups}
        onChange={(conds, groups) => {
          console.log("[Administration] ConditionEditor onChange - fulfillments_json:", data.fulfillments_json);
          handleRulesChange((prev) => ({
            ...prev,
            conditions: conds,
            conditionGroups: groups,
          }));
        }}
        dimensions={data.dimensions}
        timings={data.timings ?? []}
        allNames={allNames}
        isActive={activeSection === "conditions"}
        toggle={() =>
          setActiveSection(
            activeSection === "conditions" ? null : "conditions"
          )
        }
        userHue={data.settings.hue}
        mode={mode}
        statsData={data.fulfillments_json}
        syntaxData={data.syntaxchecks_json}
        projectId={project?.id ?? 0}
        metrics={data.metrics ?? []}
      />

      <MetricEditor
        metrics={data.metrics ?? []}
        onChange={(metrics) =>
          handleRulesChange((prev) => ({ ...prev, metrics }))
        }
        dimensions={data.dimensions || []}
        timings={data.timings ?? []}
        allNames={allNames}
        isActive={activeSection === "metrics"}
        toggle={() =>
          setActiveSection(activeSection === "metrics" ? null : "metrics")
        }
        userHue={data.settings.hue}
        mode={mode}
        projectId={project?.id ?? 0}
        syntaxData={data.syntaxchecks_json}
      />

      <ObjectiveEditor
        objectives={data.objectives ?? []}
        onChange={(objectives) =>
          handleRulesChange((prev) => ({ ...prev, objectives }))
        }
        dimensions={data.dimensions || []}
        metrics={data.metrics ?? []}
        allNames={allNames}
        isActive={activeSection === "objectives"}
        toggle={() =>
          setActiveSection(activeSection === "objectives" ? null : "objectives")
        }
        userHue={data.settings.hue}
        mode={mode}
        projectId={project?.id ?? 0}
        syntaxData={data.syntaxchecks_json}
      />

      <FixationEditor
        fixations={data.fixations ?? []}
        onChange={(fixations) =>
          handleRulesChange((prev) => ({ ...prev, fixations }))
        }
        dimensions={data.dimensions || []}
        allNames={allNames}
        isActive={activeSection === "fixations"}
        toggle={() =>
          setActiveSection(activeSection === "fixations" ? null : "fixations")
        }
        userHue={data.settings.hue}
        mode={mode}
        projectId={project?.id ?? 0}
        syntaxData={data.syntaxchecks_json}
      />

      <AdjustmentEditor
        adjustments={data.adjustments ?? []}
        onChange={(adjs) =>
          handleRulesChange((prev) => ({ ...prev, adjustments: adjs }))
        }        dimensions={data.dimensions}
        allNames={allNames}
        isActive={activeSection === "adjustments"}
        toggle={() =>
          setActiveSection(activeSection === "adjustments" ? null : "adjustments")
        }
        userHue={data.settings.hue}
        mode={mode}
        projectId={project?.id ?? 0}
        syntaxData={data.syntaxchecks_json}
      />

      {/* Code-Editor als einklappbare Sektion - synchronisiert mit den anderen Editoren */}
      <div
        className="settings-panel"
        style={{
          background: getBaseColor(
            2,
            "section",
            data.settings.hue ?? 180,
            mode
          ),
        }}
      >
        {/* Header */}
        <div
          className="settings-panel__header"
          onClick={() =>
            setActiveSection(activeSection === "xml" ? null : "xml")
          }
        >
          <div className="settings-panel__title">
            {activeSection === "xml" ? "▾" : "▸"} Code
          </div>
        </div>

        {activeSection === "xml" && (
          <div className="settings-panel__content">
            <textarea
              id="xml-editor"
              value={xmlText}
              onChange={(e) => setXmlText(e.target.value)}
              className="text-editor-textarea"
            />
            {isSaving && <p className="text-editor-saving">Speichere...</p>}
          </div>
        )}
      </div>

    </>
  );
}
